import { Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import {
  CBM_OPERATORS,
  PLAN_TRIGGER_KINDS,
  type CbmOperator,
  type PlanTriggerKind,
} from '@/features/maintenance-plans/types';
import type { PlanTriggerCreateValues } from '@/features/maintenance-plans/schema';

interface TriggerFormModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** True while the create mutation is in flight. Disables submit. */
  loading: boolean;
  /** ApiError-translated message, rendered as a destructive Alert
   *  inside the dialog so failed submits don't dismiss the modal. */
  errorMessage?: string | null;
  /** Called with the validated payload. Caller is responsible for
   *  the actual mutation invocation — modal stays presentation-only. */
  onConfirm: (payload: PlanTriggerCreateValues) => void;
}

/**
 * TriggerFormModal — adds a new plan trigger.
 *
 * The trigger schema is a discriminated union by `kind`. The modal
 * renders a kind <select> at the top and swaps the input cluster
 * underneath every time the kind changes. Each kind only collects
 * its required fields:
 *
 *   time     intervalDays (required) + optional anchorDate
 *   counter  counterField (required) + intervalValue (required) +
 *            optional seedLastFiredValue
 *   cbm      counterField (required) + cbmOperator (required) +
 *            cbmThreshold (required)
 *
 * Validation runs locally before invoking onConfirm so the parent
 * doesn't have to deal with kind-shaped error states. Field state
 * is reset whenever the dialog opens so a freshly-opened modal is
 * always blank — switching kinds also clears the now-irrelevant
 * fields so a previously-typed value doesn't sneak into the next
 * payload.
 */
export function TriggerFormModal({
  open,
  onOpenChange,
  loading,
  errorMessage,
  onConfirm,
}: TriggerFormModalProps) {
  const { t } = useTranslation();
  const [kind, setKind] = useState<PlanTriggerKind>('time');

  // Common to all kinds
  const [isActive, setIsActive] = useState(true);

  // time
  const [intervalDays, setIntervalDays] = useState('');
  const [anchorDate, setAnchorDate] = useState('');

  // counter + cbm
  const [counterField, setCounterField] = useState('');

  // counter
  const [intervalValue, setIntervalValue] = useState('');
  const [seedLastFiredValue, setSeedLastFiredValue] = useState('');

  // cbm
  const [cbmOperator, setCbmOperator] = useState<CbmOperator>('gte');
  const [cbmThreshold, setCbmThreshold] = useState('');

  // Reset everything every time the dialog opens.
  useEffect(() => {
    if (open) {
      setKind('time');
      setIsActive(true);
      setIntervalDays('');
      setAnchorDate('');
      setCounterField('');
      setIntervalValue('');
      setSeedLastFiredValue('');
      setCbmOperator('gte');
      setCbmThreshold('');
    }
  }, [open]);

  // Compute submit eligibility per kind.
  const canSubmit = (() => {
    if (loading) return false;
    if (kind === 'time') {
      const n = Number(intervalDays);
      return Number.isFinite(n) && n >= 1 && n <= 36500;
    }
    if (kind === 'counter') {
      const n = Number(intervalValue);
      return (
        counterField.trim().length > 0 &&
        Number.isFinite(n) &&
        n > 0
      );
    }
    // cbm
    const n = Number(cbmThreshold);
    return counterField.trim().length > 0 && Number.isFinite(n);
  })();

  const submit = () => {
    if (!canSubmit) return;
    if (kind === 'time') {
      onConfirm({
        kind: 'time',
        isActive,
        intervalDays: Number(intervalDays),
        ...(anchorDate ? { anchorDate } : {}),
      });
    } else if (kind === 'counter') {
      onConfirm({
        kind: 'counter',
        isActive,
        counterField: counterField.trim(),
        intervalValue: intervalValue.trim(),
        ...(seedLastFiredValue.trim() !== ''
          ? { seedLastFiredValue: Number(seedLastFiredValue) }
          : {}),
      });
    } else {
      onConfirm({
        kind: 'cbm',
        isActive,
        counterField: counterField.trim(),
        cbmOperator,
        cbmThreshold: cbmThreshold.trim(),
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {t('maintenancePlan.triggerForm.title')}
          </DialogTitle>
          <DialogDescription>
            {t('maintenancePlan.triggerForm.description')}
          </DialogDescription>
        </DialogHeader>

        {errorMessage && (
          <Alert variant="destructive">
            <AlertTitle>
              {t('common.error', { defaultValue: 'Error' })}
            </AlertTitle>
            <AlertDescription>{errorMessage}</AlertDescription>
          </Alert>
        )}

        <div className="space-y-3">
          {/* Kind selector — drives the input cluster below */}
          <div className="space-y-1">
            <label className="text-sm font-medium" htmlFor="trigger-kind">
              {t('maintenancePlan.triggerForm.kindLabel')}
            </label>
            <select
              id="trigger-kind"
              value={kind}
              onChange={(e) => setKind(e.target.value as PlanTriggerKind)}
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              {PLAN_TRIGGER_KINDS.map((k) => (
                <option key={k} value={k}>
                  {t(`maintenancePlan.triggerKind.${k}`)}
                </option>
              ))}
            </select>
            <p className="text-xs text-muted-foreground">
              {t(`maintenancePlan.triggerForm.${kind}Hint`)}
            </p>
          </div>

          {/* Kind-specific clusters */}
          {kind === 'time' && (
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1">
                <label
                  className="text-sm font-medium"
                  htmlFor="trigger-interval-days"
                >
                  {t('maintenancePlan.triggerForm.intervalDays')}
                </label>
                <Input
                  id="trigger-interval-days"
                  type="number"
                  min="1"
                  max="36500"
                  value={intervalDays}
                  onChange={(e) => setIntervalDays(e.target.value)}
                />
              </div>
              <div className="space-y-1">
                <label
                  className="text-sm font-medium"
                  htmlFor="trigger-anchor-date"
                >
                  {t('maintenancePlan.triggerForm.anchorDate')}
                </label>
                <Input
                  id="trigger-anchor-date"
                  type="date"
                  value={anchorDate}
                  onChange={(e) => setAnchorDate(e.target.value)}
                />
                <p className="text-xs text-muted-foreground">
                  {t('maintenancePlan.triggerForm.anchorDateHint')}
                </p>
              </div>
            </div>
          )}

          {kind === 'counter' && (
            <>
              <div className="space-y-1">
                <label
                  className="text-sm font-medium"
                  htmlFor="trigger-counter-field"
                >
                  {t('maintenancePlan.triggerForm.counterField')}
                </label>
                <Input
                  id="trigger-counter-field"
                  placeholder="running_hours"
                  value={counterField}
                  onChange={(e) => setCounterField(e.target.value)}
                />
                <p className="text-xs text-muted-foreground">
                  {t('maintenancePlan.triggerForm.counterFieldHint')}
                </p>
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="space-y-1">
                  <label
                    className="text-sm font-medium"
                    htmlFor="trigger-interval-value"
                  >
                    {t('maintenancePlan.triggerForm.intervalValue')}
                  </label>
                  <Input
                    id="trigger-interval-value"
                    placeholder="500"
                    value={intervalValue}
                    onChange={(e) => setIntervalValue(e.target.value)}
                  />
                </div>
                <div className="space-y-1">
                  <label
                    className="text-sm font-medium"
                    htmlFor="trigger-seed-value"
                  >
                    {t('maintenancePlan.triggerForm.seedLastFiredValue')}
                  </label>
                  <Input
                    id="trigger-seed-value"
                    placeholder=""
                    value={seedLastFiredValue}
                    onChange={(e) => setSeedLastFiredValue(e.target.value)}
                  />
                  <p className="text-xs text-muted-foreground">
                    {t('maintenancePlan.triggerForm.seedLastFiredValueHint')}
                  </p>
                </div>
              </div>
            </>
          )}

          {kind === 'cbm' && (
            <>
              <div className="space-y-1">
                <label
                  className="text-sm font-medium"
                  htmlFor="trigger-cbm-field"
                >
                  {t('maintenancePlan.triggerForm.counterField')}
                </label>
                <Input
                  id="trigger-cbm-field"
                  placeholder="vibration"
                  value={counterField}
                  onChange={(e) => setCounterField(e.target.value)}
                />
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="space-y-1">
                  <label
                    className="text-sm font-medium"
                    htmlFor="trigger-cbm-operator"
                  >
                    {t('maintenancePlan.triggerForm.cbmOperator')}
                  </label>
                  <select
                    id="trigger-cbm-operator"
                    value={cbmOperator}
                    onChange={(e) =>
                      setCbmOperator(e.target.value as CbmOperator)
                    }
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    {CBM_OPERATORS.map((op) => (
                      <option key={op} value={op}>
                        {t(`maintenancePlan.cbmOperator.${op}`)}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="space-y-1">
                  <label
                    className="text-sm font-medium"
                    htmlFor="trigger-cbm-threshold"
                  >
                    {t('maintenancePlan.triggerForm.cbmThreshold')}
                  </label>
                  <Input
                    id="trigger-cbm-threshold"
                    placeholder="4.5"
                    value={cbmThreshold}
                    onChange={(e) => setCbmThreshold(e.target.value)}
                  />
                </div>
              </div>
            </>
          )}

          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
            />
            {t('maintenancePlan.triggerForm.startActive')}
          </label>
        </div>

        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={loading}
          >
            {t('common.cancel')}
          </Button>
          <Button onClick={submit} disabled={!canSubmit}>
            {loading && <Loader2 className="animate-spin" aria-hidden />}
            {t('maintenancePlan.triggerForm.submit')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
